import { Component } from "react"

import { Col, Form } from "react-bootstrap"
import { Typeahead } from "react-bootstrap-typeahead"
import { concat, of, Subject, Subscription } from "rxjs"
import { filter, map, switchMap } from "rxjs/operators"

import { OrderDefault } from "../../api/Administration/OrderDefaults"
import EmployeeCategoriesGetObservable from "../../api/LabService/EmployeeCategoriesGet"
import EmployeeCategory from "../../api/LabService/LabServiceModel/EmployeeCategory"
import RegulatoryMode from "../../api/LabService/LabServiceModel/RegulatoryMode"
import LabServiceRegulatoryModesGetObservable from "../../api/LabService/LabServiceRegulatoryModesGet"
import { DefaultLookupCode } from "../Administration/OrderDefaults"

interface RegulatoryModeProps {
  defaults?: OrderDefault[]
  regulatoryModeID?: number
  employeeCategoryID?: number
  onChange: (isValid: boolean, mode?: RegulatoryMode, cat?: EmployeeCategory) => void
}

interface RegulatoryModeState {
  regulatoryModesLoading: boolean
  categoriesLoading: boolean
  regulatoryModes: RegulatoryMode[]
  filteredModes: RegulatoryMode[]
  categories: EmployeeCategory[]
  regulatoryModeObj?: RegulatoryMode
  categoryObj?: EmployeeCategory
}

export default class RegulatoryModeControl extends Component<RegulatoryModeProps, RegulatoryModeState> {
  state: RegulatoryModeState = {
    regulatoryModesLoading: false,
    categoriesLoading: false,
    regulatoryModes: [],
    filteredModes: [],
    categories: [],
    regulatoryModeObj: undefined,
    categoryObj: undefined,
  }

  subscriptions = new Subscription()

  regulatoryMode$ = new Subject<RegulatoryMode>()

  componentDidMount() {
    const { employeeCategoryID, defaults, regulatoryModeID } = this.props
    const { regulatoryModeObj } = this.state

    this.subscriptions.add(
      this.regulatoryMode$.subscribe((selected) =>
        this.setState({
          regulatoryModeObj: selected,
          categories: [],
        })
      )
    )

    this.subscriptions.add(
      this.regulatoryMode$
        .pipe(
          filter((selected) => !!selected),
          switchMap((selected: RegulatoryMode) =>
            concat(
              of({ isLoading: true }),
              EmployeeCategoriesGetObservable(selected.RegulatoryModeID).pipe(
                map((data) => ({ isLoading: false, result: data }))
              )
            )
          )
        )
        .subscribe((data: { isLoading: boolean; result?: EmployeeCategory[] }) => {
          const items = data.result || []
          const categoryObj = items.find((item) => item.EmployeeCategoryID === employeeCategoryID)
          if (regulatoryModeObj?.RegulatoryModeID.toString() === "3") {
            const filteredCategories: EmployeeCategory[] = []
            const FAADefaults = defaults?.filter((item) => item.LookupCode === DefaultLookupCode.FAAEmployee)
            if (FAADefaults && FAADefaults.length > 0) {
              const defaultFAA = FAADefaults.find((item) => !!item.Show && !!item.IsDefault)
              items.forEach((item) => {
                const def = FAADefaults?.find((el) => el.FieldName === item.Name)
                if (!def || def?.Show) filteredCategories.push(item)
              })
              if (defaultFAA) {
                this.setState({
                  categories: filteredCategories,
                  categoriesLoading: data.isLoading,
                  categoryObj: filteredCategories.find((item) => item.Name === defaultFAA?.FieldName),
                })
              } else {
                this.setState({ categories: filteredCategories, categoriesLoading: data.isLoading })
              }
            } else {
              this.setState({ categoriesLoading: data.isLoading, categories: items })
            }
          } else {
            this.setState({
              categoriesLoading: data.isLoading,
              categories: items,
              categoryObj,
            })
          }
          this.onCategoryChange(categoryObj)
        })
    )

    this.setState({ regulatoryModesLoading: true })
    this.subscriptions.add(
      LabServiceRegulatoryModesGetObservable(true).subscribe(
        (result: RegulatoryMode[]) => {
          const regMode = result.find((item) => item.RegulatoryModeID === regulatoryModeID)
          this.setState({
            regulatoryModes: result,
            regulatoryModesLoading: false,
            regulatoryModeObj: regMode,
          })

          if (regMode) {
            this.regulatoryMode$.next(regMode)
          } else {
            this.filterFields()
          }
        },
        () => this.setState({ regulatoryModesLoading: false })
      )
    )
  }

  componentDidUpdate(prevProps: RegulatoryModeProps) {
    const { defaults, regulatoryModeID } = this.props
    if (defaults !== prevProps.defaults && !regulatoryModeID) {
      this.filterFields()
    }
  }

  componentWillUnmount() {
    this.subscriptions.unsubscribe()
  }

  onCategoryChange = (selected?: EmployeeCategory) => {
    const { onChange } = this.props
    const { regulatoryModeObj } = this.state
    this.setState({ categoryObj: selected })
    onChange(this.isValidSelection(selected), regulatoryModeObj, selected)
  }

  filterFields = () => {
    const { defaults } = this.props
    const { regulatoryModes } = this.state
    let modeDefaults = defaults?.filter((item) => item.LookupCode === DefaultLookupCode.RegMode)
    if (modeDefaults && modeDefaults.length === 0)
      modeDefaults = defaults?.filter((item) => item.LookupCode === DefaultLookupCode.RegMode && !item.AccountID)
    if (modeDefaults && modeDefaults.length > 0) {
      const filteredModes: RegulatoryMode[] = []
      const defaultMode = modeDefaults.find((item) => !!item.Show && !!item.IsDefault)
      regulatoryModes.forEach((item) => {
        const def = modeDefaults?.find((el) => el.FieldName === item.Name)
        if (!def || def?.Show) filteredModes.push(item)
      })
      this.setState({ filteredModes })
      if (defaultMode) this.regulatoryMode$.next(filteredModes.find((item) => item.Name === defaultMode?.FieldName))
    } else {
      this.setState({ filteredModes: [...regulatoryModes] })
    }
  }

  isValidSelection = (category?: EmployeeCategory) => {
    const { categories, categoriesLoading } = this.state
    return !!category || (categories.length === 0 && !categoriesLoading)
  }

  render() {
    const { regulatoryModesLoading, regulatoryModeObj, filteredModes, categoriesLoading, categories, categoryObj } =
      this.state
    const { regulatoryModeID, employeeCategoryID } = this.props

    return (
      <>
        <Form.Row>
          <Col xs="12">
            <Form.Group>
              <Form.Label className="font-weight-bold required">REGULATORY MODE</Form.Label>
              <Typeahead
                id="regulatoryModeTypeahead"
                labelKey="Name"
                filterBy={(option, props) => {
                  return props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                }}
                disabled={regulatoryModesLoading || !!regulatoryModeID}
                isInvalid={!regulatoryModeObj}
                onChange={(selected) => this.regulatoryMode$.next(selected[0])}
                options={filteredModes || []}
                placeholder="Enter the Regulatory Mode name"
                selected={regulatoryModeObj ? [regulatoryModeObj] : []}
              />
            </Form.Group>
          </Col>
        </Form.Row>
        <Form.Row>
          <Col xs="12">
            <Form.Group>
              <Form.Label className="font-weight-bold required">CATEGORY</Form.Label>
              {!categoriesLoading && categories.length === 0 && <Form.Text>None</Form.Text>}
              {(categories.length > 0 || categoriesLoading) && (
                <Typeahead
                  id="regulatoryModeCategoryTypeahead"
                  labelKey="Name"
                  filterBy={(option, props) => {
                    return (
                      props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1
                    )
                  }}
                  onChange={(selected) => this.onCategoryChange(selected[0])}
                  isInvalid={!categoryObj}
                  options={categories}
                  disabled={categoriesLoading || !!employeeCategoryID}
                  placeholder="Enter the Category name"
                  selected={categoryObj ? [categoryObj] : []}
                />
              )}
            </Form.Group>
          </Col>
        </Form.Row>
      </>
    )
  }

  static defaultProps = { defaults: [], regulatoryModeID: undefined, employeeCategoryID: undefined }
}
